<script setup lang="ts">
import { EDate, EEmptyState, ESkeleton } from '@erpx/ui'
import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

import AppIcon from '@/components/AppIcon.vue'
import { problemText } from '@/i18n/problem'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { actionLabel, auditTimeline, listAuditActions } from '@/modules/audit/api'
import { useAuthStore } from '@/stores/auth'

/**
 * One record's audit history — the right rail on every document (§5, §11).
 *
 * Give it an entity and an id and it fetches `GET /audit/timeline`; that is the
 * whole contract, which is what lets any screen drop it in without knowing
 * anything about the audit layer. It renders what the log holds and nothing
 * else: no synthesised "record viewed" entry, no gap-filling.
 *
 * The diff is deliberately summarised to changed field names here. The rail is
 * 320px wide; the full before/after viewer lives in the Audit Explorer, one
 * click away, where there is room to read it.
 */

const props = withDefaults(
  defineProps<{
    entity: string
    entityId: string
    limit?: number
  }>(),
  { limit: 20 },
)

const auth = useAuthStore()

const { data: actions } = useQuery({
  queryKey: QUERY_KEYS.auditActions,
  queryFn: listAuditActions,
  staleTime: Infinity,
})

const { data, isPending, error } = useQuery({
  queryKey: computed(() => QUERY_KEYS.auditTimeline(props.entity, props.entityId)),
  queryFn: () => auditTimeline(props.entity, props.entityId, props.limit),
  enabled: computed(() => props.entity !== '' && props.entityId !== ''),
})

const entries = computed(() => data.value ?? [])

/** The field names a diff touched, so the rail says *what* changed, not just that. */
function changedFields(before: unknown, after: unknown): string {
  const keys = new Set<string>()
  for (const source of [before, after]) {
    if (typeof source === 'object' && source !== null) {
      for (const key of Object.keys(source as Record<string, unknown>)) keys.add(key)
    }
  }
  return [...keys].join(', ')
}
</script>

<template>
  <div v-if="isPending" role="status" aria-live="polite">
    <span class="visually-hidden">{{ $t('audit.trail.loading') }}</span>
    <ESkeleton variant="row" :lines="4" />
  </div>

  <p v-else-if="error" class="alert" role="alert">{{ problemText(error) }}</p>

  <EEmptyState
    v-else-if="entries.length === 0"
    compact
    :title="$t('audit.trail.emptyTitle')"
    :description="$t('audit.trail.emptyDescription')"
  />

  <ol v-else class="timeline">
    <li v-for="entry in entries" :key="entry.id" class="timeline__item">
      <span class="timeline__marker">
        <AppIcon name="history" :size="11" />
      </span>

      <div>
        <p class="timeline__label">{{ actionLabel(entry.action, actions ?? []) }}</p>
        <p class="timeline__meta">
          <EDate :value="entry.at" preset="dateTime" :time-zone="auth.timeZone" show-zone />
          <template v-if="entry.actorId">
            · {{ $t('audit.trail.actorUser', { id: entry.actorId }) }}
          </template>
          <template v-else> · {{ entry.actorType }}</template>
        </p>
        <p v-if="entry.note" class="timeline__meta">{{ entry.note }}</p>
        <p v-else-if="changedFields(entry.before, entry.after) !== ''" class="timeline__meta">
          {{ $t('audit.trail.changed', { fields: changedFields(entry.before, entry.after) }) }}
        </p>
      </div>
    </li>
  </ol>
</template>
